/**
 * PATH: packages/ui/src/kanban/components/KanbanCardPeekHandle.tsx
 * FILE_ID: UI-KANBAN-CARD-PEEK-HANDLE-001
 * PURPOSE: PROMOTED, labelled "Thread" trigger (in the hover action zone) that
 *   reveals the AI "thread peek" — a right-side slide-in popover.
 * WHY:
 *   FEAT-2026-06-20-KANBAN-CARD-AI-THREAD → -HOVER-ZONE. The first cut hid the
 *   thread behind a 14px right-edge chevron tab — operator: "you don't have the
 *   thread feature of trello here … that icon hanging there looks shitty". So
 *   the trigger is PROMOTED to a labelled pill that lives in the in-flow action
 *   zone the card grows on hover. Hovering the pill (short intent delay) opens
 *   the peek to the card's right; it stays open while the pointer is over the
 *   pill OR the panel; click pins it (touch / keyboard); Esc / outside-click
 *   closes a pinned peek. Reveal is owned by the zone (no self opacity gating).
 * FLOW:
 *   ┌──────────────┐  hover/click  ┌───────────────┐   ┌────────────────┐
 *   │ Thread pill  │ ────────────▶ │ open + anchor │ → │ <KanbanCardPeek>│
 *   │ (in zone)    │  intent timer │ rect (getBCR) │   │ portal popover  │
 *   └──────────────┘               └───────────────┘   └────────────────┘
 * DEPENDENCIES:
 *   - ./KanbanCardPeek: the slide-in popover this opens.
 *   - lucide-react MessageSquareText: the pill glyph (a "conversation").
 * RELATED:
 *   - ./KanbanCardActionZone.tsx: renders this as its `threadSlot`.
 *   - ./KanbanCard.tsx: wires it when the host supplies `api.getCardAiThread`
 *     AND the card has a linked email.
 */
import { useEffect, useRef, useState } from 'react'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { MessageSquareText } from 'lucide-react'

import type { KanbanApi, KanbanMember } from '../api'
import type { Card as KanbanCardModel } from '../types'
import { useIsPhone } from '../useIsPhone'
import { KanbanCardPeek } from './KanbanCardPeek'
import { aiThreadQueryKey, cardCommentsQueryKey } from './peekQueryKeys'

type Rect = { top: number; right: number; left: number; bottom: number }

const OPEN_DELAY_MS = 140
const CLOSE_DELAY_MS = 300
// Pointer keep-alive padding (px). The panel sits a GAP away from the pill and
// can be vertically clamped on-screen, so the strict pill/panel rects leave a
// dead zone the operator's pointer crosses — the peek "vanished when you moved
// your mouse over it". Inflating the hit region bridges that gap.
const KEEP_ALIVE_MARGIN = 28

export interface KanbanCardPeekHandleProps {
  cardId: string
  cardTitle: string
  api: Pick<KanbanApi, 'getCardAiThread'> &
    Partial<Pick<KanbanApi, 'listComments' | 'createComment' | 'deleteComment'>>
  /** Card hover state. The action zone owns the reveal; we also use the
   *  rising edge to PRELOAD the thread + comments so opening is instant. */
  visible: boolean
  /** Whether the card has a linked-email AI thread. When false the peek opens
   *  comment-only (manual cards) — the AI summary/conversation half is hidden
   *  and `getCardAiThread` is never prefetched/fetched. Defaults true so the
   *  email-card callers (and existing tests) behave exactly as before. */
  threadEnabled?: boolean
  /** Promote an AI action item to a real checklist step (FE stage 3). Passed
   *  straight through to the peek; omit to keep action items read-only. */
  onAddChecklistStep?: (text: string) => Promise<void>
  /** The full card — forwarded to the peek's identity/meta strip. */
  card?: KanbanCardModel
  /** Resolve assignee + comment-author ids to members. */
  resolveMember?: (userId: number) => KanbanMember | undefined
  /** Scope comment cache invalidation to a board when known. */
  boardId?: string
  /** Current viewer id — forwarded to the peek so its comment draft is scoped
   *  to this user. Absent → device-scoped 'anon' draft. */
  currentUserId?: string | number | null
}

/** Promoted, labelled "Thread" pill + the slide-in peek it opens. Self-
 *  contained: all open/close intent timing lives here so KanbanCard's diff
 *  stays tiny. Rendered in-flow inside the hover action zone. */
export function KanbanCardPeekHandle({
  cardId,
  cardTitle,
  api,
  visible,
  threadEnabled = true,
  onAddChecklistStep,
  card,
  resolveMember,
  boardId,
  currentUserId,
}: KanbanCardPeekHandleProps) {
  const qc = useQueryClient()
  // PERF-2026-08-01-KANBAN-OPEN-DEFER-AFTER — card-hover prefetch of comments /
  // AI thread must not share the connection pool with a cold progressive
  // snapshot. Key shape: ['kanban','cards',boardId,'snapshot',...].
  const openSnapshotBusy =
    useIsFetching({
      predicate: (query) => {
        const key = query.queryKey
        return (
          Array.isArray(key)
          && key[0] === 'kanban'
          && key[1] === 'cards'
          && key[3] === 'snapshot'
        )
      },
    }) > 0
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  const [anchor, setAnchor] = useState<Rect | null>(null)
  // Phones have no hover to reveal the tab and the peek renders as a bottom
  // sheet, so the tab stays tappable at rest and we don't close on scroll
  // (a bottom sheet is viewport-docked, not card-anchored).
  const isPhone = useIsPhone()
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function clearTimers() {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null }
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null }
  }

  function captureAnchor() {
    const btn = btnRef.current
    if (!btn) return
    // BUG-2026-07-06-KANBAN-THREAD-PEEK-STUCK: anchor to the CARD TILE, not
    // the pill. The pill lives in the collapsible hover action zone at the
    // card's bottom edge, so anchoring there floated the panel below / away
    // from the card the operator clicked ("not even sticking aligned to the
    // card"). The card root carries data-card-id (KanbanCard.tsx).
    const cardEl = btn.closest('[data-card-id]')
    const r = (cardEl ?? btn).getBoundingClientRect()
    setAnchor({ top: r.top, right: r.right, left: r.left, bottom: r.bottom })
  }

  function doOpen() {
    captureAnchor()
    setOpen(true)
  }

  function armOpen() {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null }
    if (open) return
    if (openTimer.current) clearTimeout(openTimer.current)
    openTimer.current = setTimeout(doOpen, OPEN_DELAY_MS)
  }

  function armClose() {
    if (pinned) return
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null }
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS)
  }

  function close() {
    clearTimers()
    setPinned(false)
    setOpen(false)
  }

  // Esc closes a pinned peek. On desktop, a BOARD/PAGE scroll also closes (the
  // anchored popover would otherwise detach from the moving card) — but a
  // scroll inside the panel itself is exempt (see onScroll). On phone the sheet
  // is viewport-docked, so scroll must NOT close it (and the scrim handles
  // tap-away).
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onKey)
    let onScroll: ((e: Event) => void) | undefined
    if (!isPhone) {
      onScroll = (e: Event) => {
        // The peek's own content scrolls (conversation + comments overflow).
        // Only a board/page scroll should detach the anchored popover — a
        // scroll INSIDE the panel must NOT close it, or reading the thread
        // (or scrolling to the comment box) makes it vanish.
        const t = e.target as Node | null
        if (panelRef.current && t && panelRef.current.contains(t)) return
        close()
      }
      window.addEventListener('scroll', onScroll, true)
    }
    return () => {
      window.removeEventListener('keydown', onKey)
      if (onScroll) window.removeEventListener('scroll', onScroll, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isPhone])

  useEffect(() => () => clearTimers(), [])

  // BUG-2026-07-06-KANBAN-THREAD-PEEK-STUCK: a PINNED peek (opened by click)
  // ignored every outside click and just hung around - the header comment
  // promised "Esc / outside-click closes a pinned peek" but no outside-click
  // listener ever existed. Capture-phase pointerdown so it fires even though
  // the panel stopPropagation()s its own clicks; anything inside the panel or
  // the pill is exempt. Phones keep the bottom-sheet scrim as their tap-away.
  useEffect(() => {
    if (!open || isPhone) return
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node | null
      if (!t) return
      if (panelRef.current?.contains(t) || btnRef.current?.contains(t)) return
      close()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isPhone])

  // Desktop hover keep-alive. The old handoff relied on the pill's mouseleave
  // arming a close that the panel's mouseenter had to cancel in time — but the
  // GAP between them (plus the panel's on-screen vertical clamp) left a dead
  // zone the pointer crossed, so the peek "vanished when you moved your mouse
  // over it". Instead, while open we track the pointer against an inflated
  // union of the pill + panel rects: anywhere near either keeps it open; only a
  // genuine exit arms the close. Pinned peeks ignore hover (Esc / scroll-away
  // close them); phones use the bottom-sheet scrim.
  useEffect(() => {
    if (!open || pinned || isPhone) return
    const within = (r: DOMRect | undefined, x: number, y: number) =>
      !!r &&
      x >= r.left - KEEP_ALIVE_MARGIN &&
      x <= r.right + KEEP_ALIVE_MARGIN &&
      y >= r.top - KEEP_ALIVE_MARGIN &&
      y <= r.bottom + KEEP_ALIVE_MARGIN
    const onMove = (e: MouseEvent) => {
      const near =
        within(btnRef.current?.getBoundingClientRect(), e.clientX, e.clientY) ||
        within(panelRef.current?.getBoundingClientRect(), e.clientX, e.clientY)
      if (near) {
        if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null }
      } else if (!closeTimer.current) {
        armClose()
      }
    }
    document.addEventListener('mousemove', onMove)
    return () => document.removeEventListener('mousemove', onMove)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pinned, isPhone])

  // PRELOAD on the card-hover rising edge (operator: "it should be preloaded").
  // The pill only appears on hover, and opening it has a 140ms intent delay, so
  // kicking the fetches off the moment the card is hovered means the peek opens
  // with data already in cache — no "Summarising…" skeleton. prefetchQuery
  // respects staleTime so a re-hover of a warm card is a no-op (no spam).
  // PERF-2026-08-01 — skip while OPEN snapshot is still on the wire.
  useEffect(() => {
    if (!visible || openSnapshotBusy) return
    // Only prefetch the AI summary for cards that actually have an email thread
    // — manual cards open comment-only, so skipping this keeps every hover on a
    // big board from firing a pointless `getCardAiThread` round-trip.
    if (threadEnabled && api.getCardAiThread) {
      void qc.prefetchQuery({
        queryKey: aiThreadQueryKey(cardId),
        queryFn: () => api.getCardAiThread!(cardId),
        staleTime: 5 * 60_000,
      })
    }
    if (api.listComments) {
      void qc.prefetchQuery({
        queryKey: cardCommentsQueryKey(cardId),
        queryFn: () => api.listComments!(cardId),
        staleTime: 30_000,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, cardId, openSnapshotBusy])

  // When the card stops being hovered and the peek isn't pinned/panel-hovered,
  // the close timer (armed on tab mouseleave) handles it. If the tab hides
  // mid-open without a leave (e.g. drag), make sure we don't strand the panel.
  useEffect(() => {
    if (!visible && !pinned && open) armClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={threadEnabled ? 'Show AI thread' : 'Show comments'}
        aria-expanded={open}
        title={threadEnabled ? 'Show AI thread' : 'Show comments'}
        onMouseEnter={armOpen}
        onMouseLeave={armClose}
        onClick={(e) => {
          e.stopPropagation()
          if (open && pinned) { close(); return }
          clearTimers()
          setPinned(true)
          doOpen()
        }}
        onMouseDown={(e) => e.stopPropagation()}
        // Stop the touch from arming the card's long-press / drag when the
        // operator is just tapping the pill to open the sheet.
        onTouchStart={(e) => e.stopPropagation()}
        className={[
          'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-medium transition-colors',
          // A faint violet pill at rest (border + tint) so the Thread trigger
          // reads as its own distinct control, not a stray icon crowding the
          // action rail (operator 2026-06-30: "thread icon and button … not
          // very clear they overlap other icons").
          'border border-violet-200 bg-violet-50/60 text-violet-700 hover:bg-violet-100 dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-300 dark:hover:bg-violet-500/20',
          open ? 'bg-violet-100 dark:bg-violet-500/20' : '',
        ].join(' ')}
      >
        <MessageSquareText className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>Thread</span>
      </button>

      {open && anchor && (
        <KanbanCardPeek
          cardId={cardId}
          cardTitle={cardTitle}
          api={api}
          threadEnabled={threadEnabled}
          anchorRect={anchor}
          onClose={close}
          panelRef={panelRef}
          onAddChecklistStep={onAddChecklistStep}
          card={card}
          resolveMember={resolveMember}
          boardId={boardId}
          currentUserId={currentUserId}
          onMouseEnter={() => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null } }}
          onMouseLeave={armClose}
        />
      )}
    </>
  )
}
